import React, { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../services/auth';
import { useTestMode } from '../contexts/TestModeContext';
import { apiService } from '../services/api';
import { platformAdminService } from '../services/platformAdmin';
import { User, UserRole } from '../types';
import ConfirmDialog from './ConfirmDialog';

export default function ImpersonationBanner() {
  const { user, impersonation, stopImpersonation, startImpersonation } = useAuthStore();
  const { isTestMode, toggleTestMode, loading: testModeLoading } = useTestMode();
  const [isLoading, setIsLoading] = useState(false);
  
  // User switching state
  const [users, setUsers] = useState<User[]>([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [switchingUser, setSwitchingUser] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const bannerRef = useRef<HTMLDivElement>(null);
  
  // Confirmation dialog state
  const [showSwitchConfirm, setShowSwitchConfirm] = useState(false);
  const [userToSwitch, setUserToSwitch] = useState<{ id: string; email: string; displayName: string } | null>(null);

  const handleStopImpersonation = async () => {
    try {
      setIsLoading(true);
      await stopImpersonation();
    } catch (error) {
      console.error('Failed to stop impersonation:', error);
      alert('Failed to stop impersonation. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleTestMode = async () => {
    try {
      await toggleTestMode();
    } catch (error) {
      console.error('Failed to toggle test mode:', error);
      alert('Failed to toggle test mode. Please try again.');
    }
  };

  // Fetch users for switching
  const fetchUsers = async () => {
    if (loadingUsers || users.length > 0) return;
    
    try {
      setLoadingUsers(true);
      
      // Check if the original admin is System Admin (regardless of impersonation status)
      // When impersonating, we want to show all users that the original system admin can see
      const isOriginalSystemAdmin = impersonation?.isImpersonating 
        ? true // If we're in impersonation mode, the original admin must be a System Admin
        : user?.role === UserRole.SYSTEM_ADMIN;
      
      if (isOriginalSystemAdmin) {
        // Use platform admin endpoint when original admin is System Admin
        const response = await platformAdminService.getUsers(1, 1000); // Get a large number to show all users
        if (response && response.users) {
          // Convert PlatformAdminUser to User type and filter out System Admins and current user
          const convertedUsers: User[] = response.users.map(u => ({
            id: u.id,
            accountId: u.accountId,
            email: u.email,
            displayName: u.displayName,
            firstName: u.firstName,
            lastName: u.lastName,
            address: u.address,
            phone: u.phone,
            profileImageUrl: u.profileImageUrl,
            role: u.role as UserRole, // Convert string to UserRole enum
            isActive: u.isActive,
            invitedAt: u.invitedAt,
            activatedAt: u.activatedAt,
            lastLoginAt: u.lastLoginAt,
            lastRealLoginAt: u.lastRealLoginAt,
            createdAt: u.createdAt,
            accountName: u.accountName, // Include account name for System Admins
          }));
          
          const filteredUsers = convertedUsers.filter(u => 
            u.id !== user?.id
          );
          setUsers(filteredUsers);
        }
      } else {
        // Regular users (non-System Admins) use the regular users endpoint
        const response = await apiService.getUsers();
        if (response.success) {
          // Filter out current user only
          const filteredUsers = response.data.filter(u => 
            u.id !== user?.id
          );
          setUsers(filteredUsers);
        }
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Handle user switching - show confirmation dialog
  const handleSwitchUser = (targetUserId: string, targetUserEmail: string, targetUserDisplayName: string) => {
    if (switchingUser) return;
    
    setUserToSwitch({
      id: targetUserId,
      email: targetUserEmail,
      displayName: targetUserDisplayName
    });
    setShowSwitchConfirm(true);
    setShowUserDropdown(false);
  };

  // Confirm user switch
  const confirmSwitchUser = async () => {
    if (!userToSwitch) return;
    
    try {
      setSwitchingUser(userToSwitch.id);
      setShowSwitchConfirm(false);
      await startImpersonation(userToSwitch.id);
    } catch (error) {
      console.error('Failed to switch user:', error);
      alert('Failed to switch user. Please try again.');
    } finally {
      setSwitchingUser(null);
      setUserToSwitch(null);
    }
  };

  // Cancel user switch
  const cancelSwitchUser = () => {
    setShowSwitchConfirm(false);
    setUserToSwitch(null);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /**
   * The banner is fixed to the top of the viewport, so everything below it has
   * to be offset by its height. That height is not a constant - the controls
   * wrap onto extra lines on narrow screens - so measure it and publish it as
   * a custom property rather than hard-coding a guess. The header's sticky
   * offset and the page's top padding both read it.
   */
  const isBannerVisible = !!(impersonation?.isImpersonating && user);

  useEffect(() => {
    const root = document.documentElement;
    const element = bannerRef.current;

    const clear = () => root.style.setProperty('--impersonation-banner-height', '0px');

    if (!isBannerVisible || !element) {
      clear();
      return;
    }

    const publishHeight = () =>
      root.style.setProperty('--impersonation-banner-height', `${element.offsetHeight}px`);

    publishHeight();

    const observer = new ResizeObserver(publishHeight);
    observer.observe(element);

    return () => {
      observer.disconnect();
      clear();
    };
  }, [isBannerVisible]);

  // Filter users based on search query
  const filteredUsers = users.filter(u => 
    u.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (u.displayName && u.displayName.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (u.firstName && u.firstName.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (u.lastName && u.lastName.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (u.accountName && u.accountName.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Only show banner if currently impersonating
  if (!impersonation?.isImpersonating || !user) {
    return null;
  }

  return (
    <div
      ref={bannerRef}
      className="fixed top-0 left-0 right-0 z-[60] bg-amber-500 border-b-2 border-amber-600 px-4 py-2 shadow-lg"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center space-x-2 md:space-x-4 flex-wrap">
          <div className="flex items-center space-x-2">
            <svg 
              className="w-5 h-5 text-amber-900" 
              fill="currentColor" 
              viewBox="0 0 20 20"
            >
              <path 
                fillRule="evenodd" 
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" 
                clipRule="evenodd" 
              />
            </svg>
            <span className="font-semibold text-amber-900">
              You are impersonating
            </span>
          </div>
          
          <div className="text-amber-900 min-w-0">
            <span className="font-medium truncate block sm:inline">{user.email}</span>
            <span className="ml-0 sm:ml-2 text-sm block sm:inline">
              (ID: {user.id?.substring(0, 8)}...)
            </span>
            {user.role && (
              <span className="ml-0 sm:ml-2 mt-1 sm:mt-0 inline-block px-2 py-1 bg-amber-600 text-amber-100 text-xs rounded-full">
                {user.role}
              </span>
            )}
          </div>

          {/* User Switching Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => {
                setShowUserDropdown(!showUserDropdown);
                if (!showUserDropdown) {
                  fetchUsers();
                }
              }}
              className="flex items-center space-x-1 bg-amber-600 hover:bg-amber-700 text-amber-100 px-3 py-1 rounded-md text-sm font-medium transition-colors duration-200"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
              <span>Switch User</span>
              <svg className={`w-4 h-4 transition-transform duration-200 ${showUserDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showUserDropdown && (
              <div className="absolute top-full left-0 mt-1 w-80 max-w-[calc(100vw-2rem)] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-[70]">
                {/* Search Input */}
                <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>

                {/* Users List */}
                <div className="max-h-60 overflow-y-auto">
                  {loadingUsers ? (
                    <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                      <svg className="animate-spin w-5 h-5 mx-auto mb-2" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Loading users...
                    </div>
                  ) : filteredUsers.length === 0 ? (
                    <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                      {searchQuery ? 'No users found matching your search.' : 'No users available for impersonation.'}
                    </div>
                  ) : (
                    filteredUsers.map((targetUser) => (
                      <button
                        key={targetUser.id}
                        onClick={() => handleSwitchUser(targetUser.id!, targetUser.email, targetUser.displayName || targetUser.email)}
                        disabled={switchingUser === targetUser.id}
                        className="w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-gray-900 dark:text-gray-100">
                              {targetUser.displayName || targetUser.email}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {targetUser.email}
                            </div>
                            {user?.role === UserRole.SYSTEM_ADMIN && targetUser.accountName && (
                              <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                Account: {targetUser.accountName}
                              </div>
                            )}
                            {targetUser.role && (
                              <span className={`inline-block mt-1 px-2 py-1 text-xs rounded-full ${
                                targetUser.role === UserRole.AUCTIONEER
                                  ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300'
                                  : targetUser.role === UserRole.STAFF
                                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'
                                  : 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                              }`}>
                                {targetUser.role}
                              </span>
                            )}
                          </div>
                          {switchingUser === targetUser.id && (
                            <svg className="animate-spin w-4 h-4 text-amber-600" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          )}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Test Mode Toggle */}
          <div className="flex items-center space-x-2">
            <span className="text-amber-900 text-sm font-medium">Stripe Test Mode:</span>
            <button
              onClick={handleToggleTestMode}
              disabled={testModeLoading}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-amber-800 focus:ring-offset-2 ${
                isTestMode 
                  ? 'bg-green-600' 
                  : 'bg-gray-400'
              } ${testModeLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                  isTestMode ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`text-xs font-medium ${isTestMode ? 'text-green-800' : 'text-amber-900'}`}>
              {isTestMode ? 'TEST' : 'LIVE'}
            </span>
          </div>
        </div>

        <button
          onClick={handleStopImpersonation}
          disabled={isLoading}
          className="bg-amber-700 hover:bg-amber-800 disabled:bg-amber-600 text-white px-4 py-1 rounded-md text-sm font-medium transition-colors duration-200 flex items-center space-x-2"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Stopping...</span>
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              <span>Stop Impersonation</span>
            </>
          )}
        </button>
      </div>

      {/* User Switch Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showSwitchConfirm}
        title="Switch User"
        message={
          userToSwitch 
            ? `Are you sure you want to switch to impersonating ${userToSwitch.displayName} (${userToSwitch.email})? This will change your current session.`
            : ''
        }
        confirmText="Switch User"
        cancelText="Cancel"
        onConfirm={confirmSwitchUser}
        onCancel={cancelSwitchUser}
        isDestructive={false}
      />
    </div>
  );
}

